import get from "lodash.get";
import { toast } from "react-toastify";
import { replace } from "connected-react-router";

import routes from "routes/routes";

import IndicatorsActioner from "services/indicators/actioner";
import Abilities from "services/permissions/abilities";
import CustomAnalysisActioner from "services/customAnalysis/actioner";
import CommonUtils from "services/common/utils";
import ContractActioner from "services/contract/actioner";
import AuthenticationActioner from "services/authentication/actioner";
import FlowActioner from "services/flow/actioner";
import LiveActioner from "services/live/actioner";
import GlobalFiltersInitialState from "services/globalFilters/initialState";
import GlobalFiltersActioner from "services/globalFilters/actioner";
import GlobalFiltersUtils from "services/globalFilters/utils";
import IntlUtils from "services/intl/utils";
import MapActioner from "services/map/actioner";
import NonMovingCarsActioner from "services/nonMovingCars/actioner";
import PedestrianStatisticsActioner from "services/pedestrianStatistics/actioner";
import PermissionsActioner from "services/permissions/actioner";
import VisitorCharacteristicsActioner from "services/visitorCharacteristics/actioner";
import ParkStatisticsActioner from "services/parkStatistics/actioner";

import UserApi from "./api";
import UserUtils from "./utils";
import UserActionTypes from "./types";
import IntlActioner from "../intl/actioner";

class UserActioner {
  static setUser(data) {
    return {
      type: UserActionTypes.SET_USER,
      ...data,
    };
  }

  static redirectUserIfUnauthorized() {
    return (dispatch, getState) => {
      const user = get(getState(), "User");
      const requestedRoute = get(getState(), "router.location.pathname");

      const currentRoute = get(
        routes.filter((route) => get(route, "props.path") === requestedRoute),
        "[0]"
      );

      dispatch(
        PermissionsActioner.hasAccess(
          get(currentRoute, "props.accessPermission.name"),
          get(currentRoute, "props.accessPermission.subject")
        )
      ).then((access) => {
        if (!access) return dispatch(replace(UserUtils.getDefaultRoute(user)));
      });
    };
  }

  static askForNewPassword(data) {
    return (dispatch, getState) => {
      return UserApi.askForNewPassword(data)
        .then(() => {
          const intl = IntlUtils.getIntlInstance();
          const msg = intl.messages["toast.info.emailWithInfoToSetPwd"];

          toast(`${msg} : ${data}`, {
            position: toast.POSITION.BOTTOM_RIGHT,
            type: toast.TYPE.INFO,
          });
        })
        .catch((error) => {
          const intl = IntlUtils.getIntlInstance();
          const msg = intl.messages["toast.errors.errorOccured"];

          toast(msg, {
            position: toast.POSITION.BOTTOM_RIGHT,
            type: toast.TYPE.ERROR,
          });

          return Promise.reject(error);
        });
    };
  }

  static setFirstPassword(confirmToken, password) {
    return (dispatch, getState) => {
      return UserApi.setFirstPassword(confirmToken, password)
        .then(() => {
          const intl = IntlUtils.getIntlInstance();
          const msg = intl.messages["toast.info.pwdUpdated"];

          toast(`${msg}`, {
            position: toast.POSITION.BOTTOM_RIGHT,
            type: toast.TYPE.INFO,
          });
        })
        .catch((error) => {
          const intl = IntlUtils.getIntlInstance();
          const msg = intl.messages["toast.errors.errorOccured"];

          toast(msg, {
            position: toast.POSITION.BOTTOM_RIGHT,
            type: toast.TYPE.ERROR,
          });

          return Promise.reject(error);
        });
    };
  }

  static setDefaultFilterDuration = (settings) => {
    return (dispatch, getState) => {
      const globalFilterDuration = get(
        getState(),
        "GlobalFilters.duration",
        GlobalFiltersInitialState.duration
      );

      // duration hasn't been retrieved from localStorage so we define defaultApiDuration
      if (GlobalFiltersInitialState.duration === globalFilterDuration) {
        const defaultDurationFilterFromApi = get(
          settings,
          "default_nb_minutes_non_moving_cars",
          GlobalFiltersInitialState.duration
        );

        dispatch(
          GlobalFiltersActioner.setParameter(
            "duration",
            defaultDurationFilterFromApi * 60
          )
        );
      }
    };
  };

  static initContract() {
    return (dispatch, getState) => {
      const ContractPromises = [
        UserApi.getContractsConfig(),
        dispatch(ContractActioner.getContracts()),
      ];

      return Promise.all(ContractPromises).then(
        ([contractsConfig, contracts]) => {
          const contractConfig =
            contractsConfig.find((config) => config?.is_default_contract) ||
            contractsConfig?.[0];
          const defaultContract = contracts?.find(
            (contract) => contractConfig?.contract === contract?.id
          );

          dispatch(
            this.setUser({
              user: {
                ...get(getState(), "User.user", {}),
                config: contractsConfig,
              },
            })
          );

          dispatch(ContractActioner.setCurrentContract(defaultContract));
          return dispatch(UserActioner.initDataForDefaultContract());
        }
      );
    };
  }

  static initDataFromContractId(contractId) {
    return (dispatch, getState) => {
      const userConfig = getState()?.User?.user?.config || [];
      const contractConfig =
        userConfig.find((config) => config?.contract === contractId) ||
        userConfig?.[0];

      return dispatch(UserActioner.initDataFromContractConfig(contractConfig));
    };
  }

  static initDataForDefaultContract() {
    return (dispatch, getState) => {
      const userConfig = getState()?.User?.user?.config || [];
      const contractConfig =
        userConfig.find((config) => config?.is_default_contract) ||
        userConfig?.[0];

      return dispatch(UserActioner.initDataFromContractConfig(contractConfig));
    };
  }

  static initDataFromContractConfig(contractConfig) {
    return async (dispatch, getState) => {
      CommonUtils.resetColorFactoryIndex();

      const contracts = getState()?.Contract?.contracts || [];
      const selectedContract = contracts.find(
        (contract) => contract?.id === contractConfig?.contract
      );
      IntlUtils.setLocale(selectedContract);

      try {
        await dispatch(
          MapActioner.retrieveMapConfig(contractConfig?.config_map)
        );
        await dispatch(
          UserActioner.getBackofficeSettings(
            contractConfig?.backoffice_settings
          )
        );

        dispatch(UserActioner.redirectUserIfUnauthorized());
        return Promise.resolve(contractConfig);
      } catch (error) {
        return dispatch(AuthenticationActioner.logout());
      }
    };
  }

  static getBackofficeSettings(id) {
    return (dispatch, getState) => {
      return UserApi.getBackofficeSettings(id).then((backofficeSettings) => {
        const rights = [...backofficeSettings.modules];

        return Abilities.defineAbilitiesFromRights(rights).then(() => {
          dispatch(
            this.setUser({
              user: {
                ...get(getState(), "User.user", {}),
                default_tab: get(backofficeSettings, "default_tab"),
                modules: rights,
              },
            })
          );
          dispatch(UserActioner.setDefaultFilterDuration(backofficeSettings));
          dispatch(FlowActioner.setDefaultFilters(backofficeSettings));
          dispatch(FlowActioner.setTrafficInfos(backofficeSettings));
          dispatch(
            GlobalFiltersActioner.setDefaultGlobalFilters(backofficeSettings)
          );
          dispatch(
            IndicatorsActioner.setBackofficeSettingsIndicators(
              backofficeSettings.indicators
            )
          );

          const filters = GlobalFiltersUtils.formatFiltersFromApi(
            backofficeSettings?.filters
          );
          const defaultSelected = filters
            .filter((f) => f.defaultSelected)
            .map((f) => f.value);
          const areaTypeForLivePage = backofficeSettings?.filters?.map(
            (item) => item.name
          );
          dispatch(
            ParkStatisticsActioner.setFilter("areaType", defaultSelected)
          );
          dispatch(
            ParkStatisticsActioner.setAttendanceTimeLimit(
              backofficeSettings?.park?.attendance
            )
          );
          dispatch(
            NonMovingCarsActioner.setFilter("areaType", defaultSelected)
          );
          dispatch(LiveActioner.setFilter("areaType", areaTypeForLivePage));
          dispatch(
            LiveActioner.setFilter(
              "flowMaximumDurationInMinutes",
              backofficeSettings?.flow?.paths?.maximum_duration_in_minutes
            )
          );
          dispatch(
            LiveActioner.setFilter(
              "durationDistributionUnit",
              backofficeSettings?.flow?.paths?.duration_distribution_unit
            )
          );
          dispatch(
            LiveActioner.setFilter(
              "flowTypes",
              backofficeSettings?.flow?.types_allowed
            )
          );
          dispatch(
            NonMovingCarsActioner.setFilter(
              "duration",
              backofficeSettings?.default_nb_minutes_non_moving_car * 60 ||
                300 * 60
            )
          );

          dispatch(
            MapActioner.setConfigParam("floorsConfig", {
              park_stats: {
                defaultFloorSelected:
                  get(backofficeSettings, "park.default_floor_selected") || 0,
                displayedFloors:
                  get(backofficeSettings, "park.displayed_floors") || [],
              },
              non_moving_cars: {
                defaultFloorSelected:
                  get(backofficeSettings, "park.default_floor_selected") || 0,
                displayedFloors:
                  get(backofficeSettings, "park.displayed_floors") || [],
              },
              pedestrian_stats: {
                defaultFloorSelected:
                  get(
                    backofficeSettings,
                    "pedestrian.default_floor_selected"
                  ) || 0,
                displayedFloors:
                  get(backofficeSettings, "pedestrian.displayed_floors") || [],
              },
              flow: {
                defaultFloorSelected:
                  get(backofficeSettings, "flow.default_floor_selected") || 0,
                displayedFloors:
                  get(backofficeSettings, "flow.displayed_floors") || [],
              },
              live: {
                defaultFloorSelected:
                  get(backofficeSettings, "park.default_floor_selected") || 0,
                displayedFloors:
                  get(backofficeSettings, "park.displayed_floors") || [],
              },
            })
          );
          dispatch(
            PedestrianStatisticsActioner.setConfig(
              backofficeSettings.pedestrian
            )
          );
          dispatch(
            CustomAnalysisActioner.setFilter(
              "analysisAreaGroups",
              backofficeSettings.analysis_area_groups
            )
          );
          dispatch(
            VisitorCharacteristicsActioner.setFilter(
              "destinationsDistribution",
              backofficeSettings?.visitor_characteristics
                ?.destinations_distribution
                ?.destination_minimum_duration_in_minutes
            )
          );

          dispatch(
            VisitorCharacteristicsActioner.setFilter(
              "averageTimeMaximumDuration",
              backofficeSettings?.visitor_characteristics?.average_time
                ?.maximum_duration_in_minutes
            )
          );

          dispatch(
            VisitorCharacteristicsActioner.setFilter(
              "nbDaysFromDate",
              backofficeSettings?.visitor_characteristics?.nb_days_from_date
            )
          );

          dispatch(
            IntlActioner.setIntlOverrides(
              backofficeSettings.intl_overrides ?? []
            )
          );

          return backofficeSettings;
        });
      });
    };
  }
}

export default UserActioner;
